import React, { Fragment } from 'react';
import classNames from 'classnames';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { Typography } from '@mui/material';

import CloudflareResizedImage from 'components/CloudflareResizedImage';

import Styles from './TravelLocalKnowledge.module.scss';

export interface TravelLocalKnowledgeProps {
  alignImage: string;
  heroImage: {
    title: string;
    contentType: string;
    url: string;
  };
  title: string;
  localExpert: string;
  subtitle: string;
  colorPalette: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
  };
  questionsAndAnswers: {
    json: any;
  };
}

const renderHeading = (_: any, children: any) => (
  <Typography component="h3" variant="title3" mb={2} className={Styles.heading}>
    {children}
  </Typography>
);

const renderParagraph = (_: any, children: any) => (
  <Typography component="p" variant="body1" mb={4} className={Styles.paragraph}>
    {children}
  </Typography>
);

export const TravelLocalKnowledge: React.FC<TravelLocalKnowledgeProps> = ({
  alignImage,
  heroImage,
  title,
  localExpert,
  subtitle,
  colorPalette,
  questionsAndAnswers,
}) => {
  if (!heroImage || !title || !localExpert || !subtitle || !colorPalette || !questionsAndAnswers) {
    return null;
  }
  const RICHTEXT_OPTIONS = {
    renderNode: {
      [BLOCKS.HEADING_3]: renderHeading,
      [BLOCKS.PARAGRAPH]: renderParagraph,
    },
  };

  const localExpertSplit = localExpert.split(' ');

  return (
    <section
      id="local-knowledge"
      className={Styles.travelLocalKnowledge}
      data-testid="travel-local-knowledge"
      style={{
        // @ts-ignore
        '--background-position': `${alignImage.toLowerCase()}`,
        '--color-palette-primary': `#${colorPalette.primaryColor}`,
        '--color-palette-secondary': `#${colorPalette.secondaryColor}`,
        '--color-palette-accent': `#${colorPalette.accentColor}`,
      }}
    >
      <header className={Styles.header}>
        <div className={Styles.headerImageContainer}>
          <CloudflareResizedImage
            alt={title}
            layout="fill"
            src={heroImage?.url}
            objectFit="cover"
            objectPosition={alignImage}
            loading="lazy"
          />
        </div>
        <div className={classNames(Styles.headerTextWrapper, 'sl-section-container')}>
          <div className={Styles.headerText}>
            <Typography variant="title3" className={Styles.title}>
              {title}
            </Typography>
            <Typography variant="title3" align="left" className={Styles.localExpert}>
              {localExpertSplit.map((word) => (
                <Fragment key={`split-${word.toLocaleLowerCase()}`}>
                  {word}
                  <br />
                </Fragment>
              ))}
            </Typography>
          </div>
        </div>
      </header>
      <div className={classNames(Styles.textWrapper, 'sl-section-container')}>
        <Typography variant="title2" className={Styles.subtitle}>
          {subtitle}
        </Typography>
        <div className={Styles.body}>
          {documentToReactComponents(questionsAndAnswers?.json, RICHTEXT_OPTIONS)}
        </div>
      </div>
    </section>
  );
};

export default TravelLocalKnowledge;
